'use client';

import { useState, useEffect } from 'react';
import BookingSteps from './BookingSteps';
import DateSelector from './DateSelector';
import PackageSelector from './PackageSelector';
import AddOnServices from './AddOnServices';
import GuestDetailsForm from './GuestDetailsForm';
import PaymentForm from './PaymentForm';
import BookingSummary from './BookingSummary';
import ConfirmationModal from './ConfirmationModal';

interface GuestDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  specialRequests: string;
}

interface PaymentDetails {
  cardNumber: string;
  cardName: string;
  expiryDate: string;
  cvv: string;
  saveCard: boolean;
}

const BookingHubInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [guests, setGuests] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [guestDetails, setGuestDetails] = useState<GuestDetails | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingId, setBookingId] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const packages = {
    essential: { name: 'Essential Stay', price: 189 },
    premium: { name: 'Premium Experience', price: 289 },
    celebration: { name: 'Celebration Package', price: 449 },
  };

  const addOns = [
    { id: 'airport', name: 'Airport Transfer', price: 75 },
    { id: 'breakfast', name: 'Daily Breakfast', price: 25 },
    { id: 'spa', name: 'Spa Treatment', price: 120 },
    { id: 'tour', name: 'Guided City Tour', price: 95 },
    { id: 'wine', name: 'Wine Tasting', price: 85 },
    { id: 'chef', name: 'Private Chef', price: 200 },
  ];

  const handleDateSelect = (checkIn: string, checkOut: string, guestCount: number) => {
    setCheckInDate(checkIn);
    setCheckOutDate(checkOut);
    setGuests(guestCount);
    setCurrentStep(2);
  };

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId);
    setCurrentStep(3);
  };

  const handleAddOnsSelect = (addOnIds: string[]) => {
    setSelectedAddOns(addOnIds);
    setCurrentStep(4);
  };

  const handleDetailsSubmit = (details: GuestDetails) => {
    setGuestDetails(details);
    setCurrentStep(5);
  };

  const handlePaymentSubmit = (details: PaymentDetails) => {
    const generatedBookingId = `SSC${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    setBookingId(generatedBookingId);
    setShowConfirmation(true);
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    setCurrentStep(1);
    setCheckInDate('');
    setCheckOutDate('');
    setGuests(0);
    setSelectedPackage('');
    setSelectedAddOns([]);
    setGuestDetails(null);
  };

  const getPackageDetails = () => {
    if (!selectedPackage) return { name: '', price: 0 };
    return packages[selectedPackage as keyof typeof packages];
  };

  const getSelectedAddOns = () => {
    return selectedAddOns
      .map((id) => addOns.find((addOn) => addOn.id === id))
      .filter((addOn): addOn is { id: string; name: string; price: number } => addOn !== undefined);
  };

  const calculateTotal = () => {
    const packageDetails = getPackageDetails();
    const nights = checkInDate && checkOutDate
      ? Math.ceil(
          (new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : 0;
    const accommodationTotal = packageDetails.price * nights;
    const addOnsTotal = getSelectedAddOns().reduce((sum, addOn) => sum + addOn.price, 0);
    const subtotal = accommodationTotal + addOnsTotal;
    const tax = subtotal * 0.1;
    return subtotal + tax;
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:128:6" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="128" data-component-end-line="137" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-16%22%7D" className="min-h-screen bg-background pt-24 pb-16">
        <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:129:8" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="129" data-component-end-line="136" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
          <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:130:10" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="130" data-component-end-line="135" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
            <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:131:12" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="131" data-component-end-line="134" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
              <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:132:14" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="132" data-component-end-line="132" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded-lg%20w-1%2F3%22%7D" className="h-12 bg-muted rounded-lg w-1/3" />
              <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:133:14" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="133" data-component-end-line="133" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-xl%22%7D" className="h-64 bg-muted rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:143:6" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="143" data-component-end-line="203" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-16%22%7D" className="min-h-screen bg-background pt-24 pb-16">
        <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:144:8" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="144" data-component-end-line="202" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
          <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:145:10" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="145" data-component-end-line="201" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
            <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:146:12" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="146" data-component-end-line="154" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
              <h1 data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:147:14" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="147" data-component-end-line="149" data-component-file="BookingHubInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-headline%20font-bold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Complete%20Your%20Booking%22%7D" className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
                Complete Your Booking
              </h1>
              <p data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:150:14" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="150" data-component-end-line="153" data-component-file="BookingHubInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20font-body%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Secure%20your%20perfect%20getaway%20in%20just%20a%20few%20simple%20steps.%20We're%20here%20to%20make%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20your%20reservation%20seamless%20and%20stress-free.%22%7D" className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
                Secure your perfect getaway in just a few simple steps. We&apos;re here to make
                your reservation seamless and stress-free.
              </p>
            </div>

            <BookingSteps data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:156:12" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="156" data-component-end-line="156" data-component-file="BookingHubInteractive.tsx" data-component-name="BookingSteps" data-component-content="%7B%22elementName%22%3A%22BookingSteps%22%7D" currentStep={currentStep} />

            <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:158:12" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="158" data-component-end-line="200" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:159:14" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="159" data-component-end-line="186" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%22%7D" className="lg:col-span-2">
                {currentStep === 1 && <DateSelector data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:160:38" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="160" data-component-end-line="160" data-component-file="BookingHubInteractive.tsx" data-component-name="DateSelector" data-component-content="%7B%22elementName%22%3A%22DateSelector%22%7D" onDateSelect={handleDateSelect} />}
                {currentStep === 2 && (
                  <PackageSelector data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:162:18" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="162" data-component-end-line="165" data-component-file="BookingHubInteractive.tsx" data-component-name="PackageSelector" data-component-content="%7B%22elementName%22%3A%22PackageSelector%22%7D"
                    onPackageSelect={handlePackageSelect}
                    onBack={() => setCurrentStep(1)}
                  />
                )}
                {currentStep === 3 && (
                  <AddOnServices data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:168:18" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="168" data-component-end-line="171" data-component-file="BookingHubInteractive.tsx" data-component-name="AddOnServices" data-component-content="%7B%22elementName%22%3A%22AddOnServices%22%7D"
                    onAddOnsSelect={handleAddOnsSelect}
                    onBack={() => setCurrentStep(2)}
                  />
                )}
                {currentStep === 4 && (
                  <GuestDetailsForm data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:174:18" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="174" data-component-end-line="177" data-component-file="BookingHubInteractive.tsx" data-component-name="GuestDetailsForm" data-component-content="%7B%22elementName%22%3A%22GuestDetailsForm%22%7D"
                    onDetailsSubmit={handleDetailsSubmit}
                    onBack={() => setCurrentStep(3)}
                  />
                )}
                {currentStep === 5 && (
                  <PaymentForm data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:180:18" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="180" data-component-end-line="184" data-component-file="BookingHubInteractive.tsx" data-component-name="PaymentForm" data-component-content="%7B%22elementName%22%3A%22PaymentForm%22%7D"
                    totalAmount={calculateTotal()}
                    onPaymentSubmit={handlePaymentSubmit}
                    onBack={() => setCurrentStep(4)}
                  />
                )}
              </div>

              <div data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:188:14" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="188" data-component-end-line="199" data-component-file="BookingHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-1%22%7D" className="lg:col-span-1">
                {currentStep > 1 && (
                  <BookingSummary data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:190:18" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="190" data-component-end-line="197" data-component-file="BookingHubInteractive.tsx" data-component-name="BookingSummary" data-component-content="%7B%22elementName%22%3A%22BookingSummary%22%7D"
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    guests={guests}
                    packageName={getPackageDetails().name}
                    packagePrice={getPackageDetails().price}
                    addOns={getSelectedAddOns()}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal data-component-id="src/app/booking-hub/components/BookingHubInteractive.tsx:205:6" data-component-path="src/app/booking-hub/components/BookingHubInteractive.tsx" data-component-line="205" data-component-end-line="209" data-component-file="BookingHubInteractive.tsx" data-component-name="ConfirmationModal" data-component-content="%7B%22elementName%22%3A%22ConfirmationModal%22%7D"
        isOpen={showConfirmation}
        bookingId={bookingId}
        onClose={handleConfirmationClose}
      />
    </>
  );
};

export default BookingHubInteractive;